// The queue's view sidebar — GET /api/views (docs/API.md §3.6).
//
// A view is a saved set of conditions the server compiles into SQL; this component
// only ever sends its id. It deliberately cannot express what a view contains,
// because the condition vocabulary lives in the server's registry allow-list and
// nowhere else.
//
// Read-only by design. Creating and editing views belongs to the admin console
// (design §7), so there is no "+ New view" affordance here — a button that opened
// nothing would be worse than its absence.

const ITEM_BASE =
  'block w-full truncate rounded-md px-2 py-1.5 text-left text-sm transition-colors';

// The same active/inactive pair the top nav uses (web/shell/PlatformHeader.jsx). Views are
// navigation, so they should feel like the rest of the app's navigation rather than
// inventing a second selected-state vocabulary.
const ITEM_ACTIVE = 'bg-sunken font-medium text-ink';
const ITEM_IDLE = 'text-ink-secondary hover:bg-sunken hover:text-ink';

function ViewItem({ view, active, onSelect }) {
  return (
    <li>
      <button
        type="button"
        // aria-current, not aria-pressed: these behave as navigation between
        // queues, and only one can be current.
        aria-current={active ? 'true' : undefined}
        title={view.name}
        onClick={() => onSelect(view.id)}
        className={`${ITEM_BASE} ${active ? ITEM_ACTIVE : ITEM_IDLE}`}
      >
        {view.name}
      </button>
    </li>
  );
}

function Group({ label, views, activeId, onSelect }) {
  if (views.length === 0) return null;
  return (
    <>
      <p className="px-2 pb-1 pt-3 text-xs font-medium uppercase tracking-wide text-ink-secondary">
        {label}
      </p>
      <ul className="space-y-0.5">
        {views.map((view) => (
          <ViewItem
            key={view.id}
            view={view}
            active={view.id === activeId}
            onSelect={onSelect}
          />
        ))}
      </ul>
    </>
  );
}

/**
 * @param {object} props
 * @param {Array<{id: string, name: string, is_shared: boolean}>} props.views
 * @param {'loading'|'ready'|'error'} props.status
 * @param {string} props.activeId  '' when no view is applied.
 * @param {(id: string) => void} props.onSelect
 */
export default function ViewPicker({ views, status, activeId, onSelect }) {
  // Shared and private views are split rather than listed together because the two
  // name spaces are independently unique — a private view may legitimately be called
  // "All unsolved" while a shared one already is (see the partial indexes in
  // migration 012). Ungrouped, those two entries would be indistinguishable.
  const shared = views.filter((view) => view.is_shared);
  const mine = views.filter((view) => !view.is_shared);

  return (
    <nav aria-label="Views" className="rounded-lg border border-line bg-surface p-2">
      <ul className="space-y-0.5">
        {/* The way back out. Without it, applying a view would be a one-way door —
            the only escape being to hand-edit the address bar. */}
        <li>
          <button
            type="button"
            aria-current={activeId === '' ? 'true' : undefined}
            onClick={() => onSelect('')}
            className={`${ITEM_BASE} ${activeId === '' ? ITEM_ACTIVE : ITEM_IDLE}`}
          >
            All tickets
          </button>
        </li>
      </ul>

      <Group label="Shared" views={shared} activeId={activeId} onSelect={onSelect} />
      <Group label="Yours" views={mine} activeId={activeId} onSelect={onSelect} />

      {/* Views are a convenience layered over a queue that works without them, so a
          failed fetch says so quietly and leaves the filter bar alone — the same
          posture as the assignee picker in List.jsx. */}
      {status === 'error' && (
        <p className="px-2 pb-1 pt-3 text-xs text-ink-muted">Views unavailable.</p>
      )}
      {status === 'loading' && views.length === 0 && (
        <p className="px-2 pb-1 pt-3 text-xs text-ink-muted">Loading…</p>
      )}
    </nav>
  );
}
